import * as React from 'react';
import { UnitInterface } from './shared';
import { UnitState } from 'src/services/graphql/Models';
import { Header, Tab, Form, Modal, Icon } from 'semantic-ui-react';
import LocationInput from 'src/components/inputs/LocationInput';
import ConfigurationsInput from 'src/components/inputs/ConfigurationsInput';
import ClientOptions from  'src/container/Client/ClientOptions';
import uuidv1 from 'uuid/v1';
import './UnitEdit.scss';
import { inject, observer } from 'mobx-react';
import { AuthStore } from '../../stores/authStore';
import UnitGroupOptions from 'src/container/UnitGroup/UnitGroupOptions';
import UnitChart from './UnitChart';

const unitStateOptions = [
    {
        key: UnitState.disabled,
        value: UnitState.disabled,
        text: 'Disabled'
    },
    {
        key: UnitState.demo,
        value: UnitState.demo,
        text: 'Demo'
    },
    {
        key: UnitState.prototyped,
        value: UnitState.prototyped,
        text: 'Prototyped'
    },
    {
        key: UnitState.test,
        value: UnitState.test,
        text: 'Test'
    },
];

interface UnitEditProps {
    fromObject: Partial<UnitInterface>;
    resolveRow: (unit?: UnitInterface) => any;
    rejectRow: (err?: Error | { isNormal: boolean, typedId: number }) => any;
    isNew?: boolean;
    authStore?: AuthStore;
}

@inject('authStore')
@observer
export default class UnitEdit extends React.Component<
    UnitEditProps, 
    any
> {
    resolveRow?: (unit?: UnitInterface) => any;
    rejectRow?: (err?: any) => any;
    typedUUID: string;

    constructor(props: UnitEditProps) {
        super(props);
        const { resolveRow, rejectRow, fromObject } = props;
        this.typedUUID = fromObject.typedId || uuidv1(); 

        this.resolveRow = resolveRow;
        this.rejectRow = rejectRow;
        
        this.state = { 
            ...fromObject,
            unitGroups: fromObject.unitGroups || []
        };
    }

    render() {
        const changeHandler = this.handleChange.bind(this);
        const submitHandler = this.handleSubmit.bind(this);
        const panes = [
            { menuItem: 'Details', render: this.renderDefault.bind(this, this.state, changeHandler) },
            { menuItem: 'Configurations', render: this.renderConfigurations.bind(this, this.state, changeHandler)  }
        ];

        if (!this.props.isNew) {
            panes.unshift({ 
                menuItem: 'Data History', render: this.renderChart.bind(this)
            });
        }

        return(
            <Modal 
                closeIcon
                size="fullscreen"
                open={true}
                onClose={this.handleClose.bind(this)}
                className="standard-padding center-margin tb-margin"
                style={{
                    marginTop: 10, 
                    marginLeft: 'auto', 
                    marginRight: 'auto',
                    padding: '21px',
                    boxSizing: 'content-box',
                    overflowY: 'scroll'
                }}
                dimmer="inverted"
                closeOnDimmerClick={true}
            >
                <Modal.Header>Editing Unit {this.state.name}</Modal.Header>
                <Form 
                    onSubmit={submitHandler}
                >
                    <Modal.Content>
                        <Modal.Description className="top-margin">
                            <Tab panes={panes} />

                        </Modal.Description>
                            
                    </Modal.Content>
                    <Modal.Actions>
                        <Form.Button primary float="right" className="top-margin">
                            Submit <Icon name="chevron right"/>
                        </Form.Button>
                    </Modal.Actions>
                </Form>
            </Modal>
        );
    }

    componentDidUpdate(props: any) {
        if (props.authStore.clientId !== this.state.clientId) {
            props.authStore.setClient(this.state.clientId);
        }

    }

    renderDefault ( 
        {
            name,
            unitState,
            clientId,
            location,
            refreshRate,
            isAlwaysOnline,
            unitGroups,
            useGroupRefereshSettings 
        }: any,
        changeHandler: any
    ) {
        const { authStore: { isSuperUser } } = this.props;
        return (
            <Tab.Pane>
                {(this.props.isNew) ?
                    <Form.Field>
                    <label>Code</label>
                    <Form.Input 
                        placeholder="Code" 
                        onChange={this.handleAliasChange.bind(this)} 
                    />
                    <small>
                        unmodifiable code used by units to communicate data from sensorType communicate.

                    </small>
                    </Form.Field>
                    : null
                }
                <Form.Field>
                    <label>Name</label>
                    <Form.Input 
                        placeholder="Name" 
                        name="name" 
                        defaultValue={name} 
                        onChange={changeHandler} 
                    />
                </Form.Field>

                <Form.Field>
                    <label>Client</label>
                    <ClientOptions 
                        search
                        name="clientId"
                        defaultValue={clientId}
                        onChange={changeHandler}
                    />
                </Form.Field>
                <Form.Field>  
                    <label>Unit Groups</label>
                    <UnitGroupOptions
                        multiple
                        onChange={changeHandler}
                        name="unitGroups"
                        presetProps={{
                            noDefault: true
                        }}
                        defaultValue={unitGroups}
                    />
                </Form.Field>
                <Form.Field>
                    <label>Unit State</label>
                    <Form.Select 
                        options={unitStateOptions}
                        name="unitState"
                        onChange={changeHandler}
                        defaultValue={unitState}
                    />
                </Form.Field>

                <Form.Field>
                    <label>Location Input</label>
                    <LocationInput 
                        name="location"
                        {...  
                            (
                                location
                                && location.lat
                                && location.lng
                            ) ?
                            {
                                lat: location.lat,
                                lng: location.lng
                            } 
                            : {}  
                        }
                        onLocationChange={changeHandler}
                    />
                </Form.Field>
                {isSuperUser ?
                    (
                        <div>
                            <Form.Field>        
                                <br/>
                                <label>Use Group Referesh Settings?</label>
                                <Form.Checkbox 
                                    toggle 
                                    name="useGroupRefereshSettings" 
                                    defaultChecked={useGroupRefereshSettings}
                                    onChange={this.handleChecked.bind(this)} 
                                />
                            </Form.Field>
                            {!useGroupRefereshSettings ?
                                (
                                    <div>
                                        <Form.Field>
                                            <label>Refresh Rate (sec)</label>
                                            <Form.Input 
                                                type="number"
                                                min="0"
                                                step="1"
                                                placeholder="Refresh Rate (sec)" 
                                                name="refreshRate" 
                                                defaultValue={refreshRate} 
                                                onChange={changeHandler} 
                                            />
                                        </Form.Field>
                                        <Form.Field>        
                                            <br/>
                                            <label>Is always Online?</label>
                                            <Form.Checkbox 
                                                toggle 
                                                name="isAlwaysOnline" 
                                                defaultChecked={isAlwaysOnline}
                                                onChange={this.handleChecked.bind(this)} 
                                            />
                                        </Form.Field>
                                    </div>
                                ) : undefined
                            }
                        </div>
                    ) : undefined
                }
            </Tab.Pane>
        );
    }

    renderConfigurations({ configurations }: any, changeHandler: any ) {
        return (
            <Tab.Pane>
                <Header>Configurations</Header>
                <Form.Field>
                    <ConfigurationsInput
                        name="configurations"
                        onChange={changeHandler}
                        initialValue={configurations || []}
                    />
                </Form.Field>
            </Tab.Pane>
        );
    }

    renderChart(chart: any) {
        return (
            <Tab.Pane>
                <UnitChart
                    fromObject={this.state}
                />
            </Tab.Pane>
        );

    }
 
    handleAliasChange(e: any, { value }: any) {
        this.setState({ typedId : `un:${value}:${this.typedUUID}`});
    }

    handleChange(
        e: any, 
        { 
            name, value 
        }: {
            name: string, 
            value: any
        }
    ) {
        this.setState({ [name]: value });
    }

    handleSubmit = (event: any) => {
        if (event.keyCode !== 13) {
            this.resolveRow(this.state as any);
        }
    }

    handleClose = () => {
        this.rejectRow({ isNormal: true, typedId: this.typedUUID});
    }

    handleChecked (e: any, {checked, name}: any) {
        this.setState({
            [name] : checked
        });
    }
}


// WEBPACK FOOTER //
// ./src/container/Unit/UnitEdit.tsx